/* =====================================================================================
   Data — the currency of the market model, and the queue of changes awaiting a decision.

   Two panels, because they answer two different questions.

     WHAT IS GOING STALE   Topics past their recheck interval. Four months by default, which
                           roughly matches how often the major registries and HTA bodies
                           actually publish. Confirming a figure is unchanged is a first-class
                           action here: without it the only way to clear an overdue topic
                           would be to invent a change to approve.

     WHAT NEEDS DECIDING   Proposed changes. Each shows the current value, the proposed value,
                           the reasoning and the evidence, so the decision can be made on this
                           screen rather than by going and looking it up.

   Three outcomes, not two. Refer exists because many of these need the person who owns that
   market rather than whoever happens to be clearing the queue, and forcing a binary choice
   means a guess gets recorded as a decision.
   ===================================================================================== */

import { useCallback, useEffect, useState } from 'react';
import { due, queue, decide, confirmCurrent, type DueTopic, type ReviewItem } from '../lib/currency';
import { fetchFreshness, markLoaded, type SourceFreshness } from '../lib/freshness';
import { fetchColleagues, type Colleague } from '../lib/chorus';
import { onFail } from '../lib/errors';
import { useDialog } from '../lib/ui';
import './datareview.css';

export function DataReviewView() {
  /* A third panel, because "what is going stale" and "what needs deciding" both assume somebody
     already knows a new release exists. This is the one that says so. */
  const [fresh, setFresh] = useState<SourceFreshness[]>([]);
  useEffect(() => { fetchFreshness().then(setFresh).catch(() => setFresh([])); }, []);
  const [topics, setTopics] = useState<DueTopic[]>([]);
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [people, setPeople] = useState<Colleague[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [refer, setRefer] = useState<ReviewItem | null>(null);
  const [reject, setReject] = useState<ReviewItem | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      due().catch(onFail('Recheck schedule', () => setTopics([]))),
      queue().catch(onFail('The review queue', () => setItems([]))),
    ]).then(([d, q]) => {
      if (Array.isArray(d)) setTopics(d);
      if (Array.isArray(q)) setItems(q);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    fetchColleagues().then(setPeople)
      .catch(onFail('Colleagues', () => setPeople([])));
  }, []);

  async function act(item: ReviewItem, decision: 'approved' | 'rejected' | 'referred',
                     note?: string, referTo?: string) {
    setBusy(item.out_id);
    try {
      await decide(item.out_id, decision, note, referTo);
      setRefer(null); setReject(null);
      reload();
    } catch (e) {
      onFail('The decision')(e);
    } finally { setBusy(null); }
  }

  const overdue = topics.filter((t) => t.overdue);

  return (
    // freshness panel rendered first: it answers "can I trust the number" before anything else
    <div className="page datareview">
      <div className="page-head">
        <h1 className="page-title">Data</h1>
        <p className="page-sub">
          Where the market model came from, when it was last checked against its source, and
          what is waiting on a decision. Nothing changes the Atlas without one.
        </p>
      </div>

      {loading && <p className="quiet-loading">Loading</p>}

      {/* --------------------------- source freshness ---------------------------
          First, because it answers "can I trust this number" before the other two panels assume
          somebody already knows a new release exists. Overdue here means the publisher has almost
          certainly issued something we have not loaded. */}
      {fresh.length > 0 && (() => {
        const overdue = fresh.filter((f) => f.state === 'overdue' || f.state === 'never loaded');
        const soon = fresh.filter((f) => f.state === 'due soon');
        return (
          <section className="dr-section">
            <h2>
              Sources
              <span className="dr-count">
                {overdue.length ? `${overdue.length} overdue` : 'all current'}
                {soon.length ? `, ${soon.length} due soon` : ''}
              </span>
            </h2>
            <p className="dr-lead">
              How often each publisher actually releases, and whether we are behind them. Only one
              of these loads itself.
            </p>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Source</th><th>What it feeds</th><th>Publishes</th>
                    <th>How it is loaded</th><th>Next due</th><th />
                  </tr>
                </thead>
                <tbody>
                  {[...overdue, ...soon, ...fresh.filter((f) => f.state === 'current')]
                    .map((f) => (
                    <tr key={f.id} className={f.state === 'overdue' ? 'dr-overdue' : ''}>
                      <td>
                        <div className="cell-title">
                          {f.source_url
                            ? <a href={f.source_url} target="_blank" rel="noreferrer">{f.source_name}</a>
                            : f.source_name}
                        </div>
                        {f.priority === 'critical' && <div className="cell-sub dr-crit">critical</div>}
                      </td>
                      <td className="cell-sub">{f.topic}</td>
                      <td className="cell-sub">
                        {f.cadence ?? 'not set'}
                        {f.release_month ? `, month ${f.release_month}` : ''}
                      </td>
                      <td className="cell-sub">
                        {/* Said plainly. A register that does not distinguish these implies more
                            automation than exists. */}
                        {f.automation === 'scheduled' ? 'runs itself'
                          : f.automation === 'scripted' ? 'script, run by hand'
                          : 'by hand'}
                      </td>
                      <td className="cell-sub">
                        {f.due_on ?? 'never loaded'}
                        {f.days_overdue > 0 && (
                          <div className="dr-late">{f.days_overdue} days late</div>
                        )}
                      </td>
                      <td className="num">
                        <button className="link-btn" onClick={async () => {
                          try {
                            await markLoaded(f.id);
                            setFresh(await fetchFreshness());
                          } catch (e) { alert(String((e as Error).message)); }
                        }}>mark loaded</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })()}

      {/* ------------------------------- the queue ------------------------------- */}
      <section className="dr-section">
        <h2 className="dr-h">
          Waiting on a decision
          {items.length > 0 && <span className="pill accent">{items.length}</span>}
        </h2>

        {!loading && !items.length && (
          <p className="module-empty">
            Nothing is waiting. Proposed changes appear here when a recheck finds that a
            published figure has moved, or when research contradicts what the Atlas holds.
          </p>
        )}

        {items.map((it) => (
          <article key={it.out_id} className="dr-item card">
            <header className="dr-item-head">
              <div>
                <strong className="dr-label">{it.out_label ?? it.out_target}</strong>
                <span className="dr-target">{it.out_target}</span>
              </div>
              {it.out_status === 'referred' && (
                <span className="pill info">referred to {it.out_referred_to ?? 'a colleague'}</span>
              )}
            </header>

            {/* The change itself, before the argument for it. */}
            <div className="dr-change">
              <span className="dr-from">{it.out_current_value ?? '—'}</span>
              <span className="dr-arrow" aria-label="becomes">→</span>
              <span className="dr-to">{it.out_proposed_value ?? '—'}</span>
            </div>

            <p className="dr-why">{it.out_rationale}</p>

            <div className="dr-meta">
              {it.out_source_name && (
                it.out_source_url
                  ? <a href={it.out_source_url} target="_blank" rel="noreferrer">{it.out_source_name}</a>
                  : <span>{it.out_source_name}</span>
              )}
              {it.out_confidence && <span className={`conf ${it.out_confidence}`}>{it.out_confidence}</span>}
              <span className="dr-origin">{it.out_origin.replace(/_/g, ' ')}</span>
            </div>

            <div className="dr-actions">
              <button className="btn-save" disabled={busy === it.out_id}
                      onClick={() => act(it, 'approved')}>
                {busy === it.out_id ? 'Applying' : 'Approve and apply'}
              </button>
              <button className="btn-compose" disabled={busy === it.out_id}
                      onClick={() => setRefer(it)}>Refer</button>
              <button className="btn-cancel" disabled={busy === it.out_id}
                      onClick={() => setReject(it)}>Refuse</button>
            </div>
          </article>
        ))}
      </section>

      {/* ------------------------------ the schedule ----------------------------- */}
      <section className="dr-section">
        <h2 className="dr-h">
          Recheck schedule
          {overdue.length > 0 && <span className="pill risk">{overdue.length} overdue</span>}
        </h2>
        <p className="dr-note">
          Public data is rechecked every four months by default. Confirming a figure is
          unchanged counts as a check and restarts the clock.
        </p>

        <table className="dr-table">
          <thead>
            <tr><th>Topic</th><th>Scope</th><th>Source</th><th>Last checked</th><th>Due</th><th /></tr>
          </thead>
          <tbody>
            {topics.map((t) => (
              <tr key={t.id} className={t.overdue ? 'dr-overdue' : ''}>
                <td>{t.topic}</td>
                <td className="muted">{t.scope ?? '—'}</td>
                <td>
                  {t.source_url
                    ? <a href={t.source_url} target="_blank" rel="noreferrer">{t.source_name}</a>
                    : t.source_name}
                </td>
                <td>{t.last_verified_on}</td>
                <td>
                  {t.next_due}
                  {t.overdue && <span className="pill risk">{t.days_overdue}d late</span>}
                </td>
                <td>
                  <button className="link-btn"
                          onClick={() => confirmCurrent(t.id)
                            .then(reload)
                            .catch(onFail('Confirming the topic'))}>
                    Still current
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {refer && <ReferDialog item={refer} people={people} onClose={() => setRefer(null)}
                             onRefer={(to, note) => act(refer, 'referred', note, to)} />}
      {reject && <RefuseDialog item={reject} onClose={() => setReject(null)}
                               onRefuse={(note) => act(reject, 'rejected', note)} />}
    </div>
  );
}

function ReferDialog({ item, people, onClose, onRefer }: {
  item: ReviewItem; people: Colleague[];
  onClose: () => void; onRefer: (to: string, note: string) => void;
}) {
  const ref = useDialog(onClose);
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf" role="dialog" aria-modal="true" aria-label="Refer this change" ref={ref}
           onClick={(e) => e.stopPropagation()}>
        <header className="cf-head"><h2>Refer this change</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button></header>
        <div className="cf-body">
          <p className="cf-note">{item.out_label}: {item.out_current_value} → {item.out_proposed_value}</p>
          <label className="cf-field"><span>Who should decide</span>
            <select value={to} onChange={(e) => setTo(e.target.value)}>
              <option value="">Choose…</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.full_name ?? p.id}</option>)}
            </select>
          </label>
          <label className="cf-field"><span>What you want them to look at</span>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="They own this market and will know whether the tariff moved." />
          </label>
          <div className="cf-actions">
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            <button className="btn-save" disabled={!to} onClick={() => onRefer(to, note)}>Refer</button>
          </div>
        </div>
      </div>
    </>
  );
}

function RefuseDialog({ item, onClose, onRefuse }: {
  item: ReviewItem; onClose: () => void; onRefuse: (note: string) => void;
}) {
  const ref = useDialog(onClose);
  const [note, setNote] = useState('');
  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf" role="dialog" aria-modal="true" aria-label="Refuse this change" ref={ref}
           onClick={(e) => e.stopPropagation()}>
        <header className="cf-head"><h2>Refuse this change</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button></header>
        <div className="cf-body">
          <p className="cf-note">{item.out_label}: {item.out_current_value} → {item.out_proposed_value}</p>
          {/* The reason is required by the database, not just by this form: without it the
              same proposal simply returns at the next recheck and is refused again. */}
          <label className="cf-field"><span>Why (recorded, and required)</span>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                      placeholder="The source is the 2022 report; the 2025 figure supersedes it." />
          </label>
          <div className="cf-actions">
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            <button className="btn-save" disabled={!note.trim()} onClick={() => onRefuse(note)}>
              Refuse
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
